import { useState, useMemo } from 'react';
import { Plus, Loader2, SprayCan, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useHousekeepingServiceTypes,
  useCreateHousekeepingServiceType,
  useUpdateHousekeepingServiceType,
  useDeleteHousekeepingServiceType,
} from '@/hooks/api/useHousekeepingServiceTypes';
import type { HousekeepingServiceType } from '@pms/shared';

// ─── Page ────────────────────────────────────────────────────────────────────

export function HousekeepingServiceTypesPage() {
  const propertyId = useSelectedPropertyId();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<HousekeepingServiceType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const listQuery = useHousekeepingServiceTypes({ propertyId }, !!propertyId);
  const deleteMutation = useDeleteHousekeepingServiceType();

  const allItems: HousekeepingServiceType[] = listQuery.data?.data ?? [];

  const items = useMemo(() => {
    if (!searchTerm) return allItems;
    const q = searchTerm.toLowerCase();
    return allItems.filter(
      (i) => i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q),
    );
  }, [allItems, searchTerm]);

  const handleDelete = (id: string) => {
    if (!window.confirm('Delete this service type?')) return;
    deleteMutation.mutate(id, {
      onError: (err) => {
        window.alert(extractErrorMessage(err));
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Housekeeping Service Types"
        description="Service types driving HK task assignment"
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            disabled={!propertyId}
          >
            <Plus size={16} aria-hidden="true" />
            Add Service Type
          </Button>
        }
      />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search service types"
        />
      </div>

      {!propertyId ? (
        <EmptyState
          icon={SprayCan}
          title="Select a property"
          description="Select a property to continue."
        />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={SprayCan}
          title="No service types"
          description="Create your first service type."
          actionLabel="Add Service Type"
          onAction={() => {
            setEditing(null);
            setShowForm(true);
          }}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Service Type</Th>
                  <Th>Code</Th>
                  <Th>Est. Minutes</Th>
                  <Th>Color / Tag</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((st) => (
                  <tr key={st.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{st.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{st.code}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {st.estimatedMinutes != null ? `${st.estimatedMinutes} min` : '--'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {st.color ? (
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="inline-block w-3.5 h-3.5 rounded-full border border-gray-200"
                            style={{ backgroundColor: st.color }}
                            aria-hidden="true"
                          />
                          <span className="font-mono text-xs text-gray-500">{st.color}</span>
                        </span>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          st.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {st.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(st);
                            setShowForm(true);
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                          aria-label="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(st.id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditing(null);
        }}
        title={editing ? 'Edit Service Type' : 'Add Service Type'}
        width="max-w-xl"
      >
        {propertyId && (
          <ServiceTypeForm
            propertyId={propertyId}
            editing={editing}
            onClose={() => {
              setShowForm(false);
              setEditing(null);
            }}
          />
        )}
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function ServiceTypeForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: HousekeepingServiceType | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [estimatedMinutes, setEstimatedMinutes] = useState(
    editing?.estimatedMinutes != null ? String(editing.estimatedMinutes) : '',
  );
  const [color, setColor] = useState(editing?.color ?? '');
  const [defaultFrequency, setDefaultFrequency] = useState(editing?.defaultFrequency ?? '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const createMutation = useCreateHousekeepingServiceType();
  const updateMutation = useUpdateHousekeepingServiceType();
  const mutation = editing ? updateMutation : createMutation;

  const isValid = name.trim().length > 0 && code.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function parseMinutes(): number | null {
    if (estimatedMinutes.trim() === '') return null;
    const n = Number(estimatedMinutes);
    return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    const minutes = parseMinutes();

    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          payload: {
            name: name.trim(),
            code: code.trim(),
            estimatedMinutes: minutes,
            color: color.trim() || null,
            defaultFrequency: defaultFrequency.trim() || null,
            isActive,
          },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        {
          propertyId,
          name: name.trim(),
          code: code.trim(),
          ...(minutes != null ? { estimatedMinutes: minutes } : {}),
          ...(color.trim() ? { color: color.trim() } : {}),
          ...(defaultFrequency.trim() ? { defaultFrequency: defaultFrequency.trim() } : {}),
          isActive,
        },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="st-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          id="st-name"
          type="text"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearError();
          }}
          placeholder="e.g. Full clean, Stayover, Turndown, Deep clean"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label htmlFor="st-code" className="block text-sm font-medium text-gray-700 mb-1">
          Code <span className="text-red-500">*</span>
        </label>
        <input
          id="st-code"
          type="text"
          required
          maxLength={100}
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="st-minutes" className="block text-sm font-medium text-gray-700 mb-1">
            Estimated Minutes
          </label>
          <input
            id="st-minutes"
            type="number"
            min={0}
            value={estimatedMinutes}
            onChange={(e) => {
              setEstimatedMinutes(e.target.value);
              clearError();
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="st-color" className="block text-sm font-medium text-gray-700 mb-1">
            Color
          </label>
          <div className="flex items-center gap-2">
            <input
              id="st-color"
              type="color"
              value={color || '#3b82f6'}
              onChange={(e) => {
                setColor(e.target.value);
                clearError();
              }}
              className="h-9 w-12 rounded border border-gray-300 p-0.5"
              aria-label="Board chip color"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => {
                setColor(e.target.value);
                clearError();
              }}
              placeholder="#3b82f6"
              maxLength={20}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="st-frequency" className="block text-sm font-medium text-gray-700 mb-1">
          Default Frequency
        </label>
        <input
          id="st-frequency"
          type="text"
          maxLength={50}
          value={defaultFrequency}
          onChange={(e) => {
            setDefaultFrequency(e.target.value);
            clearError();
          }}
          placeholder="e.g. Daily, Every 3rd night"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          id="st-active"
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="st-active" className="text-sm font-medium text-gray-700">
          Active
        </label>
      </div>

      {mutation.isError && (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(mutation.error)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" isLoading={mutation.isPending} disabled={!isValid}>
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50"
    >
      {children}
    </th>
  );
}
